import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { db } from "../App";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import TagPill from "../components/TagPill";
import ImageGrid from "../components/ImageGrid";

function safeStr(x) {
  return (x || "").toString();
}

function ProjectDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setErr("");

      try {
        const qy = query(
          collection(db, "publicProjects"),
          where("published", "==", true),
          where("slug", "==", safeStr(slug)),
          limit(1)
        );

        const snap = await getDocs(qy);
        if (snap.empty) {
          if (mounted) setItem(null);
        } else {
          const d = snap.docs[0];
          if (mounted) setItem({ id: d.id, ...d.data() });
        }
      } catch (e) {
        console.error("ProjectDetail load error:", e);
        if (mounted) setErr(e?.message || "Failed to load project.");
      }

      if (mounted) setLoading(false);
    }

    load();
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="opacity-70">Loading…</div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="container mx-auto p-4 space-y-3">
        <div className="text-red-600">{err}</div>
        <Link className="underline" to="/projects">
          Back to Projects
        </Link>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="container mx-auto p-4 space-y-3">
        <div className="opacity-80">Project not found (or not published).</div>
        <Link className="underline" to="/projects">
          Back to Projects
        </Link>
      </div>
    );
  }

  const title = safeStr(item.title) || "Untitled";
  const summary = safeStr(item.summary);
  const body = safeStr(item.body);
  const cover = safeStr(item.coverImageUrl);

  return (
    <div className="container mx-auto p-4 space-y-6">
      <div className="space-y-2">
        <Link to="/projects" className="underline opacity-80">
          ← Back to Projects
        </Link>

        <h1 className="text-3xl font-semibold">{title}</h1>
        {summary && <p className="opacity-80 max-w-3xl">{summary}</p>}

        <div className="flex flex-wrap gap-2">
          {(item.tags || []).map((t, idx) => (
            <TagPill key={`${t}-${idx}`}>{safeStr(t)}</TagPill>
          ))}
        </div>
      </div>

      <div className="border rounded overflow-hidden bg-white">
        {cover ? (
          <img src={cover} alt={title} className="w-full max-h-[520px] object-cover" />
        ) : (
          <div className="w-full h-64 flex items-center justify-center opacity-60 bg-secondary">No cover image</div>
        )}
      </div>

      {body && (
        <div className="border rounded p-4 bg-white">
          <div className="prose max-w-none whitespace-pre-wrap">{body}</div>
        </div>
      )}

      <div className="space-y-3">
        <div className="text-xl font-semibold">Gallery</div>
        <ImageGrid images={item.gallery || []} />
        {!Array.isArray(item.gallery) || item.gallery.length === 0 ? <div className="opacity-70">No gallery images.</div> : null}
      </div>
    </div>
  );
}

export default ProjectDetail;
